/**
 * The billing verbs, as sheets: approve or decline a plan change, issue an
 * invoice, mark one paid, cancel one. Shared by the Billing page and a
 * restaurant's page, so the two can never word or check them differently.
 *
 * Each says what will happen before it happens — who is emailed, what the
 * restaurant sees — because every one of them reaches a restaurant's owner.
 * Amounts are checked as text (`isAmount`); the server prices and totals.
 */

import { useState } from 'react';
import {
  displayLocale,
  formatMoney,
  useApprovePlanRequest,
  useDeclinePlanRequest,
  useIssuePlatformInvoice,
  useMarkInvoicePaid,
  useVoidInvoice,
  type Mailed,
  type PlatformInvoice,
} from '@restaurant/api-client';
import { ActionError, Badge, Button, Field, Input, Sheet } from '@restaurant/ui';

import { cycleWords, formatDay, isAmount } from './words';

/** What a plan-change sheet needs to know about the request. */
export interface RequestFacts {
  id: string;
  restaurantId: string;
  restaurantName: string;
  planName: string;
  cycle: 'month' | 'year';
  note: string;
  listPrice: string | null;
  currency: string;
}

function mailedWords(mailed: Mailed | null | undefined): string {
  if (!mailed) return '';
  const sent = mailed.sent_to.length
    ? ` Emailed to ${mailed.sent_to.join(', ')}.`
    : ' Nobody was emailed: the restaurant has no owner with a login.';
  const failed = mailed.failed.length
    ? ` Could not reach ${mailed.failed.join(', ')}; send it another way.`
    : '';
  return sent + failed;
}

export function ApproveSheet({
  request,
  onDismiss,
}: {
  request: RequestFacts;
  onDismiss: () => void;
}) {
  const approve = useApprovePlanRequest();
  const [invoice, setInvoice] = useState(true);
  const [amount, setAmount] = useState(request.listPrice ?? '');
  const [note, setNote] = useState('');
  const needsAmount = invoice && request.listPrice === null;
  const amountOk = !invoice || (amount.trim() === '' ? !needsAmount : isAmount(amount));

  if (approve.data) {
    const issued = approve.data.invoice;
    return (
      <Sheet
        title="Approved"
        onDismiss={onDismiss}
        actions={
          <Button variant="primary" size="lg" block onClick={onDismiss}>
            Done
          </Button>
        }
      >
        <p className="pf__sub">
          {request.restaurantName} is on {request.planName} from today.
          {issued
            ? ` Invoice ${issued.number} for ${formatMoney(issued.amount, issued.currency, displayLocale())} is due ${formatDay(issued.due_on, displayLocale())}.`
            : ' No invoice was issued.'}
          {mailedWords(approve.data.mailed)}
        </p>
      </Sheet>
    );
  }

  return (
    <Sheet
      title={`Move ${request.restaurantName} to ${request.planName}`}
      onDismiss={onDismiss}
      actions={
        <>
          <Button size="lg" block onClick={onDismiss}>
            Not now
          </Button>
          <Button
            variant="primary"
            size="lg"
            block
            disabled={!amountOk}
            loading={approve.isPending}
            onClick={() =>
              approve.mutate({
                restaurantId: request.restaurantId,
                requestId: request.id,
                amount: invoice && amount.trim() !== '' ? amount.trim() : null,
                invoice,
                note: note.trim(),
              })
            }
          >
            Approve
          </Button>
        </>
      }
    >
      <p className="pf__sub">
        The plan changes today, billed {cycleWords(request.cycle)}.
        {request.note ? ` They wrote: “${request.note}”` : ''}
      </p>
      <label className="pf__check">
        <input
          type="checkbox"
          checked={invoice}
          onChange={(e) => setInvoice(e.target.checked)}
        />
        Issue an invoice for the period and email it to the owners
      </label>
      {invoice && (
        <Field
          label={`Amount (${request.currency})`}
          hint={
            request.listPrice === null
              ? 'This plan has no list price. Type the price you agreed.'
              : `The list price is ${formatMoney(request.listPrice, request.currency, displayLocale())}. Change it for an agreed price.`
          }
          error={
            amount.trim() !== '' && !isAmount(amount)
              ? 'Type an amount like 59 or 59.50.'
              : undefined
          }
        >
          <Input
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
          />
        </Field>
      )}
      <Field label="Note to the restaurant" hint="Optional. They see it on their billing page.">
        <Input value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
      <ActionError error={approve.error} action="Approving the change" />
    </Sheet>
  );
}

export function DeclineSheet({
  request,
  onDismiss,
}: {
  request: RequestFacts;
  onDismiss: () => void;
}) {
  const decline = useDeclinePlanRequest();
  const [note, setNote] = useState('');
  return (
    <Sheet
      title={`Decline ${request.restaurantName}'s move to ${request.planName}`}
      tone="danger"
      onDismiss={onDismiss}
      actions={
        <>
          <Button size="lg" block onClick={onDismiss}>
            Not now
          </Button>
          <Button
            variant="danger"
            size="lg"
            block
            disabled={note.trim() === ''}
            loading={decline.isPending}
            onClick={() =>
              decline.mutate(
                {
                  restaurantId: request.restaurantId,
                  requestId: request.id,
                  note: note.trim(),
                },
                { onSuccess: onDismiss },
              )
            }
          >
            Decline
          </Button>
        </>
      }
    >
      <p className="pf__sub">
        Their plan stays as it is. They see your reason on their billing page.
      </p>
      <Field label="Why">
        <Input value={note} onChange={(e) => setNote(e.target.value)} autoFocus />
      </Field>
      <ActionError error={decline.error} action="Declining the change" />
    </Sheet>
  );
}

export function IssueInvoiceSheet({
  restaurants,
  fixedRestaurant,
  currency,
  onDismiss,
}: {
  /** Choose from these, unless the sheet was opened on one restaurant. */
  restaurants: { id: string; name: string }[];
  fixedRestaurant?: { id: string; name: string } | undefined;
  currency: string;
  onDismiss: () => void;
}) {
  const issue = useIssuePlatformInvoice();
  const [restaurantId, setRestaurantId] = useState(fixedRestaurant?.id ?? '');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const ready = restaurantId !== '' && isAmount(amount) && description.trim() !== '';

  if (issue.data) {
    const invoice = issue.data.invoice;
    return (
      <Sheet
        title={`Issued ${invoice.number}`}
        onDismiss={onDismiss}
        actions={
          <Button variant="primary" size="lg" block onClick={onDismiss}>
            Done
          </Button>
        }
      >
        <p className="pf__sub">
          {formatMoney(invoice.amount, invoice.currency, displayLocale())}, due{' '}
          {formatDay(invoice.due_on, displayLocale())}.{mailedWords(issue.data.mailed)}
        </p>
      </Sheet>
    );
  }

  return (
    <Sheet
      title="Issue an invoice"
      onDismiss={onDismiss}
      actions={
        <>
          <Button size="lg" block onClick={onDismiss}>
            Not now
          </Button>
          <Button
            variant="primary"
            size="lg"
            block
            disabled={!ready}
            loading={issue.isPending}
            onClick={() =>
              issue.mutate({
                restaurantId,
                amount: amount.trim(),
                description: description.trim(),
              })
            }
          >
            Issue and email it
          </Button>
        </>
      }
    >
      <p className="pf__sub">
        For a renewal, an extra, or anything agreed outside a plan change. It is emailed to the
        restaurant's owners with how to pay, and shows on their billing page.
      </p>
      {fixedRestaurant ? (
        <p className="pf__sub">
          To <strong>{fixedRestaurant.name}</strong>
        </p>
      ) : (
        <Field label="Restaurant">
          <select
            className="ui-input"
            value={restaurantId}
            onChange={(e) => setRestaurantId(e.target.value)}
          >
            <option value="">Choose one</option>
            {restaurants.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </Field>
      )}
      <Field
        label={currency ? `Amount (${currency})` : 'Amount'}
        error={
          amount.trim() !== '' && !isAmount(amount)
            ? 'Type an amount like 59 or 59.50.'
            : undefined
        }
      >
        <Input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
      </Field>
      <Field label="What it is for" hint="Printed on the invoice.">
        <Input value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <ActionError error={issue.error} action="Issuing the invoice" />
    </Sheet>
  );
}

export function PaidSheet({
  restaurantId,
  invoice,
  onDismiss,
}: {
  restaurantId: string;
  invoice: PlatformInvoice;
  onDismiss: () => void;
}) {
  const paid = useMarkInvoicePaid();
  const [reference, setReference] = useState('');
  return (
    <Sheet
      title={`Mark ${invoice.number} paid`}
      onDismiss={onDismiss}
      actions={
        <>
          <Button size="lg" block onClick={onDismiss}>
            Not now
          </Button>
          <Button
            variant="primary"
            size="lg"
            block
            loading={paid.isPending}
            onClick={() =>
              paid.mutate(
                { restaurantId, number: invoice.number, reference: reference.trim() },
                { onSuccess: onDismiss },
              )
            }
          >
            Mark paid
          </Button>
        </>
      }
    >
      <p className="pf__sub">
        {formatMoney(invoice.amount, invoice.currency, displayLocale())} for{' '}
        {invoice.description}. Only once the money has arrived.
      </p>
      <Field label="Payment reference" hint="Optional: the bank's reference, say.">
        <Input value={reference} onChange={(e) => setReference(e.target.value)} autoFocus />
      </Field>
      <ActionError error={paid.error} action="Marking it paid" />
    </Sheet>
  );
}

export function VoidSheet({
  restaurantId,
  invoice,
  onDismiss,
}: {
  restaurantId: string;
  invoice: PlatformInvoice;
  onDismiss: () => void;
}) {
  const voided = useVoidInvoice();
  const [reason, setReason] = useState('');
  return (
    <Sheet
      title={`Cancel ${invoice.number}`}
      tone="danger"
      onDismiss={onDismiss}
      actions={
        <>
          <Button size="lg" block onClick={onDismiss}>
            Keep it
          </Button>
          <Button
            variant="danger"
            size="lg"
            block
            loading={voided.isPending}
            onClick={() =>
              voided.mutate(
                { restaurantId, number: invoice.number, reason: reason.trim() },
                { onSuccess: onDismiss },
              )
            }
          >
            Cancel the invoice
          </Button>
        </>
      }
    >
      <p className="pf__sub">
        {formatMoney(invoice.amount, invoice.currency, displayLocale())} for{' '}
        {invoice.description}. It stays on the restaurant's billing page, marked cancelled, and
        cannot be paid. This cannot be undone.
      </p>
      <Field label="Why" hint="Optional. The restaurant sees it in its audit log.">
        <Input value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <ActionError error={voided.error} action="Cancelling the invoice" />
    </Sheet>
  );
}

/** An invoice's state in one word, with overdue said rather than coloured. */
export function InvoiceState({
  invoice,
  overdue,
}: {
  invoice: PlatformInvoice;
  overdue: boolean;
}) {
  if (invoice.status === 'paid') return <Badge tone="ok">Paid</Badge>;
  if (invoice.status === 'void') return <Badge>Cancelled</Badge>;
  return overdue ? <Badge tone="danger">Overdue</Badge> : <Badge tone="warn">Open</Badge>;
}
